import { VFC } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { ReportSentilinkApplicationRisk } from 'dashboard/models/report/integration/sentilink-application-risk';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';

const RiskAssessmentTH = styled(TableTH)`
  width: 320px;
`;

const redactedSticker = <Sticker>Redacted</Sticker>;

const MEDIUM_RISK_THRESHOLD = 451;
const HIGH_RISK_THRESHOLD = 750;
const MAX_SENTILINK_SCORE = 1000;

const getIcon = (score: number) => {
  if (score < 0) {
    return <></>;
  } else if (score < MEDIUM_RISK_THRESHOLD) {
    return <MatchIconPositive />;
  } else if (score < HIGH_RISK_THRESHOLD) {
    return <MatchIconWarning />;
  } else {
    return <MatchIconAlert />;
  }
};

interface SentilinkApplicationRiskProps {
  report: ReportSentilinkApplicationRisk;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const SentilinkApplicationRisk: VFC<SentilinkApplicationRiskProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: SentilinkApplicationRiskProps) => {
  let [sentilinkSyntheticScore, sentilinkIdTheftScore] = [-1, -1];
  if (report.attributes.result) {
    sentilinkSyntheticScore = report.attributes.result['sentilink-synthetic-score']
      ?.score as number;
    sentilinkIdTheftScore = report.attributes.result['sentilink-id-theft-score']?.score as number;
  }

  const riskAssessmentContents = (
    <>
      <Card.Title>Risk Assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <TableTH data-test='risk-assessment-table__value-header'>Value</TableTH>
          </>
        }
      >
        {sentilinkSyntheticScore != null && (
          <TableTR
            data-test='risk-assessment-table__row--synthetic-score'
            id='risk-assessment-table__row--synthetic-score'
            key='abuse-score'
          >
            <TableIconTD>{getIcon(sentilinkSyntheticScore)}</TableIconTD>
            <TableTD>
              <span>Synthetic Score</span>
              <HelpIcon popoverWidth={300}>
                Likelihood that the application identity is an instance of synthetic identity fraud.
              </HelpIcon>
            </TableTD>
            <TableTD>
              {isRedacted ? redactedSticker : `${sentilinkSyntheticScore} / ${MAX_SENTILINK_SCORE}`}
            </TableTD>
          </TableTR>
        )}
        {sentilinkIdTheftScore != null && (
          <TableTR
            data-test='risk-assessment-table__row--id-theft-score'
            id='risk-assessment-table__row--id-theft-score'
            key='id-theft-score'
          >
            <TableIconTD>{getIcon(sentilinkIdTheftScore)}</TableIconTD>
            <TableTD>
              <span>ID Theft Score</span>
              <HelpIcon popoverWidth={300}>
                Likelihood that the account is a victim of identity theft.
              </HelpIcon>
            </TableTD>
            <TableTD>
              {isRedacted ? redactedSticker : `${sentilinkIdTheftScore} / ${MAX_SENTILINK_SCORE}`}
            </TableTD>
          </TableTR>
        )}
      </TableLayout>
    </>
  );

  const applicationContents = (
    <>
      <Card.Title>SentiLink Application</Card.Title>
      <AttributeGrid colCount={2}>
        <AttributeItem
          title='Application ID'
          value={report.attributes.result?.['application-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Customer ID'
          value={report.attributes.result?.['customer-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Transaction ID'
          value={report.attributes.result?.['transaction-id']}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
        <AttributeItem
          title='Environment'
          value={report.attributes.result?.environment}
          placeholder='N/A'
          isRedacted={isRedacted}
        />
      </AttributeGrid>
    </>
  );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {riskAssessmentContents}
      {applicationContents}
    </ReportResultLayout>
  );
};

export default SentilinkApplicationRisk;
